import React, { forwardRef, useEffect, useState } from "react";
import { Breadcrumb } from "components";
import { translate } from "react-switch-lang";
import { AddBox, ArrowDownward } from "@material-ui/icons";
import MaterialTable from "material-table";
import Check from "@material-ui/icons/Check";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Clear from "@material-ui/icons/Clear";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import FilterList from "@material-ui/icons/FilterList";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import Remove from "@material-ui/icons/Remove";
import SaveAlt from "@material-ui/icons/SaveAlt";
import Search from "@material-ui/icons/Search";
import ViewColumn from "@material-ui/icons/ViewColumn";
import CircularProgress from "@material-ui/core/CircularProgress";
import PrintIcon from "@material-ui/icons/Print";
import CloudDownloadOutlinedIcon from "@material-ui/icons/CloudDownloadOutlined";
import Swal from "sweetalert2";
import {
  addTarget,
  deleteTarget,
  getTargets,
  uploadTargetFile,
} from "service/manageTargetService";
import { getRegions } from "service/manageRegionService";
import { getOutlets } from "service/manageOutletService";
import TableToolbar from "components/tableToolbar";
import ImportDataModal from "components/importDataModal";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  print: forwardRef((props, ref) => <PrintIcon {...props} ref={ref} />),
  DownloadTable: forwardRef((props, ref) => (
    <CloudDownloadOutlinedIcon
      style={{ fontSize: "larger", fontWeight: "bolder" }}
      {...props}
      ref={ref}
    />
  )),
};

const ManageTarget = (props) => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [regionLoading, setRegionLoading] = useState(true);
  const [regions, setRegions] = useState({});
  const [outletLoading, setOutletLoading] = useState(true);
  const [outlets, setOutlets] = useState({});
  const [allOutlets, setAllOutlets] = useState([]);
  const [showImportDataModal, setShowImportDataModal] = useState(false);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);

  useEffect(() => {
    getData();
    getRegionsData();
    getOutletData();
  }, []);

  const getData = async (loading = true) => {
    setLoader(loading);
    await getTargets()
      .then((res) => {
        const data = res.data || {};
        if (typeof data === "object") {
          const newData = [];
          const outletCodesArray = Object.keys(data);
          outletCodesArray.map((outlet_code) => {
            const dataArray = data[outlet_code];
            dataArray.map((t) => {
              const item = { ...t, outlet_code };
              newData.push(item);
            });
          });
          setData(newData || []);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const getRegionsData = async () => {
    setRegionLoading(true);
    await getRegions()
      .then((res) => {
        const newRegions = {};
        const data = res.data || [];
        if (typeof data === "object") {
          data.map((r) => {
            newRegions[r.region_code] = r.region_name;
          });
          setRegions(newRegions);
        }
      })
      .catch((error) => {})
      .finally(() => {
        setRegionLoading(false);
      });
  };

  const getOutletData = async () => {
    setOutletLoading(true);
    await getOutlets()
      .then((res) => {
        const newOutlets = {};
        const data = res.data || [];
        if (typeof data === "object") {
          data.map((o) => {
            newOutlets[o.outlet_code] = o.outlet_name;
          });
          setAllOutlets(data);
          setOutlets(newOutlets);
        }
      })
      .catch((error) => {})
      .finally(() => {
        setOutletLoading(false);
      });
  };

  const onAddData = (newData = {}, isUpdate) => {
    const outlet_code = newData.outlet_code;
    let region_code = null;

    allOutlets.map((r) => {
      if (r.outlet_code === outlet_code) {
        region_code = r.region_code;
      }
    });
    const data = {
      ...newData,
      region_code,
      isUpdate,
    };
    return new Promise((resolve, reject) => {
      addTarget(data)
        .then((res) => {
          getData(false).finally(() => {
            resolve(true);
          });
          if (res.status !== 200) {
            Swal.fire({
              icon: "error",
              title: res.message || "Error occured",
            });
            reject(res.error);
          } else {
            Swal.fire({
              icon: "success",
              title: `Target ${isUpdate ? "Updated" : "Added"} Successfully`,
            });
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: error.message || "Error occured",
          });
          reject(error);
        });
    });
  };

  const onDeleteData = (newData = {}) => {
    const data = [
      {
        region_code: newData.region_code,
        outlet_code: newData.outlet_code,
        month: newData.month,
      },
    ];
    return new Promise((resolve, reject) => {
      deleteTarget(data)
        .then((res) => {
          getData(false).finally(() => {
            resolve(true);
          });
          if (res.status !== 200) {
            Swal.fire({
              icon: "error",
              title: res.message || "Error occured",
            });
            reject(res.error);
          } else {
            Swal.fire({
              icon: "success",
              title: `Target Deleted Successfully`,
            });
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: error.message || "Error occured",
          });
          reject(error);
        });
    });
  };

  const uploadFile = (file) => {
    const formData = new FormData();
    formData.append("target", file);

    setUploadFileLoading(true);
    console.log("manageOutlet uploadFile", file, formData);
    uploadTargetFile(formData)
      .then((res) => {
        if (res.status === 200) {
          getData();
          setShowImportDataModal(false);
          Swal.fire({
            icon: "success",
            title: props.t("Data Imported"),
          });
        } else {
          Swal.fire({
            icon: "error",
            title: res.message || "Error occured",
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: error.message || "Error occured",
        });
      })
      .finally(() => {
        setUploadFileLoading(false);
      });
  };

  return (
    <>
      <Breadcrumb title={props.t("Manage Target")} />
      <div className="app-wrapper">
        <div>
          {loader || regionLoading || outletLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                height:
                  props.width >= 1200
                    ? "calc(100vh - 259px)"
                    : "calc(100vh - 238px)",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className="animated slideInUpTiny animation-duration-3">
              <MaterialTable
                icons={tableIcons}
                columns={[
                  {
                    title: props.t("Outlet"),
                    field: "outlet_code",
                    lookup: outlets,
                    editable: "onAdd",
                  },
                  {
                    title: props.t("Region"),
                    field: "region_code",
                    lookup: regions,
                    editable: "never",
                  },
                  {
                    title: props.t("Month"),
                    field: "month",
                    type: "numeric",
                    editable: "onAdd",
                    validate: (rowData) =>
                      rowData.month > 12 || rowData.month < 0
                        ? { isValid: false, helperText: "Should be 1 to 12" }
                        : true,
                  },
                  {
                    title: props.t("Baqati"),
                    field: "baqati",
                    type: "numeric",
                    validate: (rowData) =>
                      rowData.baqati < 0
                        ? { isValid: false, helperText: "should above 0" }
                        : true,
                  },
                  {
                    title: props.t("Hayyak"),
                    field: "hayyak",
                    type: "numeric",
                    validate: (rowData) =>
                      rowData.hayyak < 0
                        ? { isValid: false, helperText: "Should above 0" }
                        : true,
                  },
                  {
                    title: props.t("HBB"),
                    field: "hbb",
                    type: "numeric",
                    validate: (rowData) =>
                      rowData.hbb < 0
                        ? { isValid: false, helperText: "Should above 0" }
                        : true,
                  },
                  {
                    title: props.t("Upgrade"),
                    field: "upgrade",
                    type: "numeric",
                    validate: (rowData) =>
                      rowData.upgrade < 0
                        ? { isValid: false, helperText: "Should above 0" }
                        : true,
                  },
                ]}
                data={data}
                options={{
                  actionsColumnIndex: -1,
                  addRowPosition: "first",
                  showTitle: false,
                  filtering: true,
                  sorting: true,
                }}
                localization={{
                  header: {
                    actions: props.t("Action"),
                  },
                }}
                components={{
                  Toolbar: (propsToolbar) => (
                    <TableToolbar
                      onClickImport={() => setShowImportDataModal(true)}
                      {...propsToolbar}
                      t={props.t}
                    />
                  ),
                }}
                editable={{
                  onRowAdd: (data) => onAddData(data, false),
                  onRowUpdate: (data) => onAddData(data, true),
                  onRowDelete: onDeleteData,
                }}
              />
            </div>
          )}
        </div>
      </div>
      <ImportDataModal
        onImport={uploadFile}
        loading={uploadFileLoading}
        show={showImportDataModal}
        setShow={setShowImportDataModal}
      />
    </>
  );
};

export default translate(ManageTarget);
